import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  LinearProgress,
  Stack,
  Chip,
  Alert,
  Divider,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { mockConversationalQuestions } from './mockData';

const ConversationalHelperMock = ({ open, onClose, onApplyCriteria }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [customAnswers, setCustomAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const totalSteps = mockConversationalQuestions.length;
  const progress = ((currentStep) / totalSteps) * 100;

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleCustomAnswerChange = (questionId, value) => {
    setCustomAnswers({ ...customAnswers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      generateCriteria();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateCriteria = () => {
    setShowResults(true);
  };

  const getGeneratedCriteria = () => {
    const businessType = answers[1] || 'B2B SaaS Companies';
    const stage = answers[2] || 'Growth Stage (Series A-C)';
    const goal = answers[3] || 'Sales Prospecting';
    const size = answers[4] || 'Small Business (51-200 employees)';
    const exclusions = answers[5] || [];

    return {
      mustHaves: `Must be a ${businessType.toLowerCase()}\nMust be in ${stage.toLowerCase()} funding stage\nMust have ${size.toLowerCase().split('(')[1]?.replace(')', '') || '51-200 employees'}\nMust have a clear decision-making process for ${goal.toLowerCase()}`,
      
      niceToHaves: `Preferably actively growing and hiring\nIdeally has a dedicated budget for new solutions\nBonus if they're expanding to new markets\nPreferably has modern technology stack\nIdeally responsive to outreach and open to partnerships`,
      
      disqualifications: exclusions.includes('None of the above') 
        ? 'Exclude companies with recent negative news\nNo companies in hiring freezes\nAvoid companies with direct competitors as customers'
        : `Exclude ${exclusions.join(', ').toLowerCase()}\nNo companies with recent negative news\nAvoid companies in hiring freezes`
    };
  };

  const handleApply = () => {
    const criteria = getGeneratedCriteria();
    onApplyCriteria(criteria);
    onClose();
    resetHelper();
  };

  const resetHelper = () => {
    setCurrentStep(0);
    setAnswers({});
    setCustomAnswers({});
    setShowResults(false);
  };

  const handleClose = () => {
    onClose();
    resetHelper();
  };

  const currentQuestion = mockConversationalQuestions[currentStep];
  const isAnswered = answers[currentQuestion?.id] || customAnswers[currentQuestion?.id];

  if (showResults) {
    const criteria = getGeneratedCriteria();
    
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
          Generated Criteria
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            Based on your answers, we've generated optimized research criteria. Review and apply them to your research job.
          </Alert>

          <Stack spacing={3}>
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" color="error.main" gutterBottom>
                Must-Have Criteria
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {criteria.mustHaves}
              </Typography>
            </Paper>

            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" color="success.main" gutterBottom>
                Nice-to-Have Criteria
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {criteria.niceToHaves}
              </Typography>
            </Paper>

            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" color="warning.main" gutterBottom>
                Disqualification Criteria
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {criteria.disqualifications}
              </Typography>
            </Paper>
          </Stack>

          <Alert severity="info" sx={{ mt: 3 }}>
            You can still edit these criteria after applying them to fine-tune your research parameters.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowResults(false)}>
            Back to Questions
          </Button>
          <Button onClick={handleApply} variant="contained" startIcon={<AutoFixHighIcon />}>
            Apply Criteria
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AutoFixHighIcon sx={{ mr: 1 }} />
            Research Criteria Helper
          </Box>
          <Chip 
            label={`${currentStep + 1} of ${totalSteps}`} 
            variant="outlined" 
            size="small" 
          />
        </Stack>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ mt: 2, borderRadius: 1 }} 
        />
      </DialogTitle>

      <DialogContent>
        {currentQuestion && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {currentQuestion.question}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {currentQuestion.followUp}
            </Typography>

            <RadioGroup
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={option}
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                />
              ))}
            </RadioGroup>

            {currentQuestion.id === mockConversationalQuestions.length && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label="Additional Requirements (Optional)"
                  placeholder="Any specific requirements not covered above?"
                  multiline
                  rows={3}
                  value={customAnswers[currentQuestion.id] || ''}
                  onChange={(e) => handleCustomAnswerChange(currentQuestion.id, e.target.value)}
                />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Show summary of previous answers */}
            {currentStep > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Your Previous Answers:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(answers).map(([questionId, answer]) => {
                    const question = mockConversationalQuestions.find(q => q.id === parseInt(questionId));
                    if (question && parseInt(questionId) < currentQuestion.id) {
                      return (
                        <Chip
                          key={questionId}
                          label={`${question.question.split(' ')[2]}: ${answer}`}
                          variant="outlined"
                          size="small"
                        />
                      );
                    }
                    return null;
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button 
          onClick={handleBack} 
          disabled={currentStep === 0}
          variant="outlined"
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isAnswered}
          variant="contained"
          endIcon={currentStep === totalSteps - 1 ? <CheckCircleIcon /> : <ArrowForwardIcon />}
        >
          {currentStep === totalSteps - 1 ? 'Generate Criteria' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationalHelperMock;